import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Form,
  Heading,
  InlineStack,
  Link,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from '@customerio/pluma-components/react';
import {
  buildPushTestRequest,
  extractPushApiErrors,
  sendTestPush,
  writeLastTestedRecipient,
  type PushApiError,
  type PushPlatform,
} from '@/features/push-credentials';
import { isEmptyString } from '@/utils/string';

const DEVICE_TOKEN_HELP_URL = 'https://docs.customer.io/journeys/push-getting-started/#how-to-find-your-device-tokens';

const RECIPIENT_POINTER = '/data/attributes/recipient';

/** What the field says when the API rejects the token, whatever detail it gave. */
const INVALID_TOKEN_MESSAGE = 'Enter a valid device token.';

const UNCONFIRMED_ACCOUNT_MESSAGE = 'Verify your email address to send a test.';

export type AppSendTestProps = {
  workspaceId: number;
  appId: number;
  platform: PushPlatform | null;
  hasPlatforms: boolean;
  isAccountUnconfirmed: boolean;
};

function blockedReasonFor(platform: PushPlatform | null, hasPlatforms: boolean, recipient: string): string | null {
  if (platform == null) {
    return hasPlatforms ? 'Select a device type.' : 'Enable push actions.';
  }
  return isEmptyString(recipient) ? 'Enter a token.' : null;
}

function hasRecipientError(errors: PushApiError[]): boolean {
  return errors.some((error) => error.source?.pointer === RECIPIENT_POINTER);
}

/**
 * The app and platform decide which credential the backend uses, so nothing
 * about the authorization is sent with the request, only where to send it.
 */
export function AppSendTest({ workspaceId, appId, platform, hasPlatforms, isAccountUnconfirmed }: AppSendTestProps) {
  const [recipient, setRecipient] = useState('');
  const [isTokenRejected, setIsTokenRejected] = useState(false);

  const sendTest = useMutation({
    mutationFn: () =>
      sendTestPush(
        workspaceId,
        buildPushTestRequest({ to: recipient, platform: platform ?? undefined, app_id: appId }),
      ),
    onMutate: () => {
      setIsTokenRejected(false);
    },
    onSuccess: () => {
      // The push settings page offers this token back as `Last tested token`,
      // so a test sent from here saves the sender re-pasting it there.
      writeLastTestedRecipient(workspaceId, { id: recipient.trim(), platform });
    },
    onError: (error: unknown) => {
      setIsTokenRejected(hasRecipientError(extractPushApiErrors(error)));
    },
  });

  const blockedReason = blockedReasonFor(platform, hasPlatforms, recipient);
  const isSending = sendTest.isPending;
  // The account gate outranks the rest: it says why the send is refused no
  // matter what else is missing.
  const tooltip = isAccountUnconfirmed ? UNCONFIRMED_ACCOUNT_MESSAGE : blockedReason;
  const isBlocked = isAccountUnconfirmed || blockedReason != null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isBlocked || isSending) {
      return;
    }
    try {
      await sendTest.mutateAsync();
    } catch {
      // The status line and the field below say what went wrong.
    }
  };

  return (
    <Box>
      <Heading level="4" mb="100">
        Device token
      </Heading>
      <Form onSubmit={handleSubmit}>
        <InlineStack alignBlock="end" gap="100">
          <Box flexGrow="1" maxW="full" minW="0">
            <TextField
              size="sm"
              label="Recipient"
              placeholder="Device token"
              value={recipient}
              isDisabled={isSending}
              isInvalid={isTokenRejected}
              error={isTokenRejected ? INVALID_TOKEN_MESSAGE : undefined}
              onInput={(event) => setRecipient(event.target.value)}
            />
          </Box>
          <Box>
            <Tooltip isCustom={true} content={tooltip ?? undefined} isDisabled={isSending || tooltip == null}>
              {(context) => (
                <Button
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  type="submit"
                  variant="primary"
                  size="sm"
                  isDisabled={isBlocked}
                  isLoading={isSending}
                >
                  {sendTest.isIdle ? 'Send test' : 'Send again'}
                </Button>
              )}
            </Tooltip>
          </Box>
        </InlineStack>

        <InlineStack alignBlock="center" alignInline="space-between" mt="150">
          <Link href={DEVICE_TOKEN_HELP_URL} isExternal={true} variant="primary" icon="exit" iconPosition="trailing">
            How to find your device tokens
          </Link>
          <Box>
            {isSending ? <Spinner /> : null}
            {sendTest.isError ? <Text color="critical">Test failed to send.</Text> : null}
            {sendTest.isSuccess ? <Text color="success">Test sent.</Text> : null}
          </Box>
        </InlineStack>
      </Form>
    </Box>
  );
}
